import { Fieldset, Stack, Title, Text, Alert } from '@mantine/core';
import { useRef, useState } from 'react';
import { CompressionAndExport } from '@/app/workflows/actions/interfaces';
import { useFormContext } from '../../contexts/formContext';
import { SingleFileSettings } from './settings/SingleFileSettings';
import { OptionalSetting } from '../../components/OptionalSetting';
import { TextSetting } from '../../components/TextSetting';
import { ExportName } from './ExportName';
import { IconAlertTriangle } from '@tabler/icons-react';

const fileExportPath = 'compressionAndExport.fileExports';
const filenamePath = 'compressionAndExport.filename';
export type FileExport = CompressionAndExport['fileExports'][number];
const maxFilesCount = 3;

function VrmWarning() {
  return (
    <Alert
      icon={<IconAlertTriangle size="1rem" />}
      title="VRM Export Warning: VRM exports are only supported for VRM input files"
      color="grey"
    >
    </Alert>
  );
}

export function CompressionAndExportOperation() {
  const form = useFormContext();

  const { filename, fileExports } = form.getValues().compressionAndExport;
  const files = useRef<FileExport[]>(
    Array.from({ length: maxFilesCount }).map((_, i) =>
      fileExports[i]
        ? { ...fileExports[i] }
        : ({
          fileType: 'glb',
          textureFormat: {
            normal: 'auto',
            orm: 'auto',
            emissive: 'auto',
            baseColor: 'auto',
          },
          meshCompressionMethod: 'none',
        } as FileExport)
    )
  );

  const hasVrmFile = form.values.compressionAndExport.fileExports.some(file => file.fileType === 'vrm');

  const onFileSelectionChanged = (selected: boolean, index: number, fileExport: FileExport) => {
    if (selected) {
      form.insertListItem(fileExportPath, fileExport);
      return;
    }

    form.removeListItem(fileExportPath, index);
  };

  const numberOfEnabledFiles = form.values.compressionAndExport.fileExports.length;

  return (
    <Stack align='flex-start' mb='lg'>
      <Title order={5}>File Compression and Export</Title>
      <Text c='gray.6'>Settings for data compression and file export</Text>
      {hasVrmFile && <VrmWarning />}
      <Fieldset disabled={false} variant='unstyled'>
        <Stack gap='xl'>
          {files.current.map((fileExport, index) => (
            <SingleFileSettings
              key={index}
              title={`File ${index + 1}`}
              onChanged={(selected) => onFileSelectionChanged(selected, index, fileExport)}
              isEnabled={index < numberOfEnabledFiles}
              fileNumber={index}
              template={fileExport}
              canChange={index !== 0 && (index === numberOfEnabledFiles || index === numberOfEnabledFiles - 1)}
            />
          ))}
        </Stack>
        <Stack gap='md' mt='xl'>
          <OptionalSetting
            onChanged={(option) => {
              form.setFieldValue(`${filenamePath}.useId`, option);
            }}
            isEnabled={filename?.useId ?? false}
            title='Include Model ID'
          />
          <OptionalSetting
            onChanged={(option) => form.setFieldValue(`${filenamePath}.usePresetName`, option)}
            isEnabled={filename?.usePresetName ?? false}
            title='Include Preset Name'
          />
          <OptionalSetting
            onChanged={(option) => form.setFieldValue(`${filenamePath}.useSuffix`, option ? ' ' : '')}
            isEnabled={!!filename?.useSuffix ?? false}
            title='Include Custom Suffix'
          >
            <TextSetting
              value={filename?.useSuffix ?? ''}
              onChange={(value) => form.setFieldValue(`${filenamePath}.useSuffix`, value)}
              w='300px'
            />
          </OptionalSetting>
          <ExportName
            useId={filename?.useId ?? false}
            usePresetName={filename?.usePresetName ?? false}
            useSuffix={filename?.useSuffix ?? ''}
          />
        </Stack>
      </Fieldset>
    </Stack>
  );
}
